import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { CONTROL_TYPES } from "../types";
import { joinSpendingControlsWithUsage } from "../utils/joinSpendingControlsWithUsage";
import { invalidateSpendingControlsQueries } from "./invalidateSpendingControlsQueries";
import { useGetSpendingControls } from "./useGetSpendingControls";

// Gated off (`enabled`) when there's no week yet, so this placeholder is never actually sent.
const FALLBACK_WEEK_START = new Date(0);

interface UseVariableChargeSpendLimitParams {
  workplaceId: string;
  weekStartDate: Date | undefined;
  /** Surface-specific condition (e.g. dialog open / shift loaded), ANDed with the shared gating. */
  enabled: boolean;
  allowAdmin?: boolean;
}

interface UseVariableChargeSpendLimitResult {
  isEnabled: boolean;
  limitInMinorUnits: number | undefined;
  usageInMinorUnits: number;
  /** Last-updated timestamp of the variableCharge control; lets callers detect a still-pending increase request. */
  controlUpdatedAt: Date | undefined;
  isLoading: boolean;
  invalidate: () => void;
}

/**
 * Shared variableCharge spend-limit data source for every charge-editing surface: owns the gating
 * (non-admin + `CONFIGURE_SPENDING_CONTROLS` includes `variableCharge`) and a fresh-on-open weekly-usage
 * read. `refetchOnMount: "always"` + `staleTime: Infinity` bounds the expensive recompute to open (+
 * submit via `invalidate`), so window focus/reconnect don't re-aggregate. Callers derive over-limit /
 * overage from `limit`/`usage` via the `variableChargeSpendLimit` helpers.
 */
export function useVariableChargeSpendLimit(
  params: UseVariableChargeSpendLimitParams
): UseVariableChargeSpendLimitResult {
  const { workplaceId, weekStartDate, enabled, allowAdmin = false } = params;
  const queryClient = useQueryClient();

  // Skip CBH admins unless a caller opts in: workplace surfaces block the save, the admin tool only warns.
  const { admin: isAdmin } = useSession();
  const spendingControlsConfig = useCbhFlag(CbhFeatureFlag.CONFIGURE_SPENDING_CONTROLS, {
    defaultValue: { enabled: false },
  });
  const isEnabled =
    enabled &&
    // No workplace id (e.g. shift loaded without `facility.userId`) → skip, don't query `/workplaces//...`.
    workplaceId.length > 0 &&
    (allowAdmin || !isAdmin) &&
    spendingControlsConfig.enabled &&
    (spendingControlsConfig.enabledControlTypes ?? ["total"]).includes(
      CONTROL_TYPES.variableCharge
    ) &&
    isDefined(weekStartDate);

  const { data, isInitialLoading } = useGetSpendingControls(
    {
      workplaceId,
      weekStartDate: weekStartDate ?? FALLBACK_WEEK_START,
      controlType: [CONTROL_TYPES.variableCharge],
    },
    { enabled: isEnabled, staleTime: Number.POSITIVE_INFINITY, refetchOnMount: "always" }
  );

  const variableChargeControl = useMemo(
    () =>
      joinSpendingControlsWithUsage(data).find(
        (control) => control.controlType === CONTROL_TYPES.variableCharge
      ),
    [data]
  );

  const invalidate = useCallback(() => {
    invalidateSpendingControlsQueries(queryClient);
  }, [queryClient]);

  return {
    isEnabled,
    limitInMinorUnits: variableChargeControl?.amountInCents,
    usageInMinorUnits:
      (variableChargeControl?.verifiedAmountInCents ?? 0) +
      (variableChargeControl?.plannedAmountInCents ?? 0),
    controlUpdatedAt: variableChargeControl?.updatedAt,
    isLoading: isInitialLoading,
    invalidate,
  };
}
